import React, { useMemo } from 'react';
import { Spinner } from '../UI/Spinner';
import type { PositionResponse } from '../../types/api';

interface PositionsCardProps {
  positions: PositionResponse[];
  isLoading?: boolean;
}

/**
 * Formats a string currency value for display
 */
function formatCurrency(value: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

/**
 * Formats a quantity value for display
 */
function formatQuantity(value: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(num);
}

/**
 * Calculates and formats P&L percentage
 */
function calculatePnlPercent(pnl: string, marketValue: string): string {
  const pnlNum = parseFloat(pnl);
  const valueNum = parseFloat(marketValue);
  if (isNaN(pnlNum) || isNaN(valueNum) || valueNum === 0) return '0.00%';
  const costBasis = valueNum - pnlNum;
  if (costBasis === 0) return '0.00%';
  const percent = (pnlNum / costBasis) * 100;
  return `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`;
}

/**
 * Returns P&L styling based on value
 */
function getPnlStyling(value: string): { textClass: string; icon: string } {
  const num = parseFloat(value);
  if (isNaN(num) || num === 0) {
    return { textClass: 'text-gray-600', icon: '' };
  }
  if (num > 0) {
    return { textClass: 'text-green-600', icon: '▲' };
  }
  return { textClass: 'text-red-600', icon: '▼' };
}

/**
 * Positions Card component
 * Displays current positions with P&L information
 */
export function PositionsCard({ positions, isLoading = false }: PositionsCardProps) {
  // Calculate totals
  const totals = useMemo(() => {
    let totalMarketValue = 0;
    let totalUnrealizedPnl = 0;

    for (const position of positions) {
      totalMarketValue += parseFloat(position.market_value) || 0;
      totalUnrealizedPnl += parseFloat(position.unrealized_pnl) || 0;
    }

    return {
      marketValue: totalMarketValue.toString(),
      unrealizedPnl: totalUnrealizedPnl.toString(),
    };
  }, [positions]);

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Positions</h2>
        <div className="flex justify-center py-8">
          <Spinner size="md" />
        </div>
      </div>
    );
  }

  if (positions.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Positions</h2>
        <div className="text-center py-8">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No positions yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            Submit an order to start building your portfolio
          </p>
        </div>
      </div>
    );
  }

  return (
    <section aria-labelledby="positions-heading" className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 id="positions-heading" className="text-lg font-semibold text-gray-900">
          Positions ({positions.length})
        </h2>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Symbol
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Quantity
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Avg Cost
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Market Value
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                P&L
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {positions.map((position) => {
              const pnlStyle = getPnlStyling(position.unrealized_pnl);
              const hasPendingBuy = parseFloat(position.pending_buy) > 0;
              const hasPendingSell = parseFloat(position.pending_sell) > 0;

              return (
                <tr key={position.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {position.symbol}
                    </div>
                    {(hasPendingBuy || hasPendingSell) && (
                      <div className="text-xs text-gray-500">
                        {hasPendingBuy && (
                          <span className="text-green-600">
                            +{formatQuantity(position.pending_buy)} pending buy
                          </span>
                        )}
                        {hasPendingBuy && hasPendingSell && ' | '}
                        {hasPendingSell && (
                          <span className="text-red-600">
                            -{formatQuantity(position.pending_sell)} pending sell
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatQuantity(position.quantity)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(position.average_cost)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(position.market_value)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className={`text-sm font-medium ${pnlStyle.textClass}`}>
                      {pnlStyle.icon && <span className="mr-1">{pnlStyle.icon}</span>}
                      {formatCurrency(position.unrealized_pnl)}
                    </div>
                    <div className={`text-xs ${pnlStyle.textClass}`}>
                      {calculatePnlPercent(position.unrealized_pnl, position.market_value)}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {/* Totals Row */}
          <tfoot className="bg-gray-50">
            <tr>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                TOTAL
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                {/* Empty */}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                {/* Empty */}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                {formatCurrency(totals.marketValue)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right">
                {(() => {
                  const pnlStyle = getPnlStyling(totals.unrealizedPnl);
                  return (
                    <>
                      <div className={`text-sm font-semibold ${pnlStyle.textClass}`}>
                        {pnlStyle.icon && <span className="mr-1">{pnlStyle.icon}</span>}
                        {formatCurrency(totals.unrealizedPnl)}
                      </div>
                      <div className={`text-xs ${pnlStyle.textClass}`}>
                        {calculatePnlPercent(totals.unrealizedPnl, totals.marketValue)}
                      </div>
                    </>
                  );
                })()}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}
